import { roundToTwoDecimals, sumRounded } from '../../Utils/decimalUtils';
import { isTiered } from './priceSimulationModel';
import type { CalculatedTier, Calculations } from './PriceSimulationWorksheet.types';
import { billedImpact, currentAnnualValue, newAnnualValue, planHasQuantity, tierPlans, weightedIncreasePercent } from './worksheetCalculations';

export type ImpactRowLevel = 'tier' | 'bindingTime';

export interface ImpactRow {
    key: string;
    level: ImpactRowLevel;
    label: string;
    months: number | null;
    hasAnnualValue: boolean;
    hasQuantity: boolean;
    currentAnnualValue: number;
    newAnnualValue: number;
    impact: number;
    billedImpact: number;
    weightedIncrease: number;
}

export interface PackageImpact {
    packageId: string;
    packageName: string;
    hasAnnualValue: boolean;
    hasQuantity: boolean;
    currentAnnualValue: number;
    newAnnualValue: number;
    billedImpact: number;
    rows: ImpactRow[];
}

interface ImpactRowInput {
    key: string;
    level: ImpactRowLevel;
    label: string;
    months?: number;
    hasAnnualValue: boolean;
    hasQuantity: boolean;
    current: number;
    next: number;
    billed: number;
}

const toImpactRow = ({ key, level, label, months, hasAnnualValue, hasQuantity, current, next, billed }: ImpactRowInput): ImpactRow => ({
    key,
    level,
    label,
    months: months ?? null,
    hasAnnualValue,
    hasQuantity,
    currentAnnualValue: current,
    newAnnualValue: next,
    impact: roundToTwoDecimals(next - current),
    billedImpact: billed,
    weightedIncrease: weightedIncreasePercent(current, next),
});

const calculateBindingTimeRows = (tier: CalculatedTier): ImpactRow[] => {
    const plans = tierPlans(tier).filter((plan) => !isTiered(plan));
    const monthValues = [...new Set(plans.map((plan) => plan.months))].sort((a, b) => a - b);

    return monthValues.map((months) => {
        const monthPlans = plans.filter((plan) => plan.months === months);
        return toImpactRow({
            key: `${tier.tierKey}-${months}M`,
            level: 'bindingTime',
            label: '',
            months,
            hasAnnualValue: true,
            hasQuantity: monthPlans.some(planHasQuantity),
            current: sumRounded(monthPlans.map(currentAnnualValue)),
            next: sumRounded(monthPlans.map(newAnnualValue)),
            billed: sumRounded(monthPlans.map(billedImpact)),
        });
    });
};

export const calculateImpactBreakdown = (calculations: Calculations): PackageImpact[] =>
    calculations.packages.map((pkg) => ({
        packageId: pkg.packageId,
        packageName: pkg.packageName,
        hasAnnualValue: pkg.tiers.some((tier) => tier.tierHasAnnualValue),
        hasQuantity: pkg.hasQuantity,
        currentAnnualValue: pkg.currentAnnualValue,
        newAnnualValue: pkg.newAnnualValue,
        billedImpact: pkg.billedImpact,
        rows: pkg.tiers.flatMap((tier) => [
            toImpactRow({
                key: tier.tierKey,
                level: 'tier',
                label: tier.tierName,
                hasAnnualValue: tier.tierHasAnnualValue,
                hasQuantity: tier.tierHasQuantity,
                current: tier.tierCurrentAnnualValue,
                next: tier.tierNewAnnualValue,
                billed: tier.tierBilledImpact,
            }),
            ...calculateBindingTimeRows(tier),
        ]),
    }));
